import type { AssetDetail, BatteryArchive, BatteryArchiveSummary, BatteryResults, BatterySuggestion, ContextRunDetail, PipelineSummary } from '@lastrouter/contracts';
import {
  ArrowLeft,
  Badge,
  Alert,
  Button,
  Card,
  Checkbox,
  Dialog,
  DialogContent,
  CardContent,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  cn,
  Field,
  focusRing,
  Info,
  Input,
  NativeSelect,
  Play,
  Search,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  X,
} from '@lastrouter/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { errorMessage, useApiMutation, useApiQuery } from '../../api/hooks.js';
import { useSession } from '../../app/session.js';
import { StatusBadge } from '../../components/common.js';
import { formatDateTime, formatNumber } from '../../lib/format.js';
import { CancelRunButton } from './pipeline.js';
import { ASSET_QUERIES } from './versions.js';

/*
 * A bateria, num cartão só. O agente recebe cada pergunta e monta a consulta; o Lastrouter compara a fórmula montada com a
 * do gabarito. Nada vai à engine e nenhuma resposta em texto é gravada: a tela mostra fórmulas, nunca valores.
 * Lista das rodadas (com o mini-gráfico de cada uma) e, ao clicar, o detalhe: barrinhas, melhorias e perguntas.
 * Rodada em andamento aparece direto no detalhe. Explicação fica no ícone de informação, não em parágrafo.
 */

type Row = BatteryResults['rows'][number];
type Filter = 'all' | 'wrong' | 'right' | 'pending';

const SIZES = [5, 10, 15, 25] as const;

/** Sem acento e sem caixa, para a busca achar "educação" em "EDUCACAO". */
const plain = (s: string | null) => (s ?? '').normalize('NFKD').replace(/\p{Mn}/gu, '').toLowerCase();
const TONE: Record<Row['resultado'], 'success' | 'danger' | 'accent' | 'neutral' | 'warning'> = {
  certo: 'success',
  errado: 'danger',
  'sem resposta': 'danger',
  respondido: 'accent',
  aguardando: 'neutral',
  'não avaliado': 'warning',
};
const MATCH: Record<Filter, (r: Row) => boolean> = {
  all: () => true,
  wrong: (r) => r.resultado === 'errado' || r.resultado === 'sem resposta',
  right: (r) => r.resultado === 'certo',
  pending: (r) => r.resultado === 'aguardando' || r.resultado === 'respondido' || r.resultado === 'não avaliado',
};

/** Cor e altura de cada barrinha: feita em cheio, esperando baixa. */
const SEG: Record<Row['resultado'], string> = {
  certo: 'h-full bg-success',
  errado: 'h-full bg-danger',
  'sem resposta': 'h-full bg-danger',
  respondido: 'h-full bg-accent',
  'não avaliado': 'h-full bg-warning',
  aguardando: 'h-1/3 bg-border-strong',
};

/**
 * Progresso quebrado: uma barrinha por pergunta, na ordem. Passar o mouse cresce a barrinha e abre na hora a pergunta e a
 * fórmula que o agente montou; clicar mostra só ela na tabela.
 */
function Segments({ rows, label, picked, onPick }: { rows: Row[]; label: string; picked: string | null; onPick: (r: Row) => void }) {
  const { t } = useTranslation();
  const k = 'assets.batteryRun';
  return (
    <div role="img" aria-label={label} className="flex h-12 items-end gap-[2px] pt-2">
      {rows.map((r) => (
        <Tooltip
          key={r.id}
          side="top"
          delay={0}
          content={
            <span className="flex max-w-80 flex-col gap-1 py-0.5">
              <span className="font-semibold">
                {r.id} · {t(`${k}.results.${r.resultado}`)}
              </span>
              <span>{r.pergunta}</span>
              <span className="break-all font-mono opacity-80">{r.formula_agente ? `${t(`${k}.agentFormula`)}: ${r.formula_agente}` : t(`${k}.noFormulaYet`)}</span>
            </span>
          }
        >
          <button
            type="button"
            tabIndex={-1}
            aria-label={`${r.id}: ${r.pergunta}`}
            onClick={() => onPick(r)}
            className="group flex h-full min-w-[4px] max-w-3 flex-1 cursor-pointer items-end justify-center"
          >
            <span
              className={cn(
                'w-[3px] origin-bottom rounded-full transition-[height,background-color,transform,width] duration-200 ease-out group-hover:w-[5px] group-hover:scale-y-[1.18]',
                SEG[r.resultado],
                picked === r.id && 'w-[5px] scale-y-[1.18]',
              )}
            />
          </button>
        </Tooltip>
      ))}
    </div>
  );
}

/** Explicação sob demanda: ícone de informação com tooltip, no lugar de parágrafo na tela. */
export function InfoTip({ text, label }: { text: string; label: string }) {
  return (
    <Tooltip content={<span className="block max-w-80">{text}</span>} delay={0}>
      <button type="button" aria-label={label} className={cn('inline-flex size-5 shrink-0 cursor-help items-center justify-center rounded-full text-fg-muted hover:text-fg', focusRing)}>
        <Info className="size-4" aria-hidden="true" />
      </button>
    </Tooltip>
  );
}

/** Mini-gráfico da lista: uma barrinha por pergunta, na ordem (verde certa, vermelha errada, baixa e cinza não feita). */
function MiniMarks({ marks, label }: { marks: string; label: string }) {
  return (
    <span role="img" aria-label={label} className="flex h-5 w-40 max-w-full items-end gap-px">
      {[...marks].map((m, i) => (
        <span key={i} className={cn('min-w-px max-w-1 flex-1 rounded-full', m === 'c' ? 'h-full bg-success' : m === 'e' ? 'h-full bg-danger' : 'h-1/3 bg-border-strong')} />
      ))}
    </span>
  );
}

/** Mínimo para a bateria ficar OK. Não impede publicar: é a régua da certificação (nota_minima). */
const MINIMUM = 80;

/** O que a melhoria muda no contexto, como diferença: linhas que entram, em verde. */
function SuggestionDiff({ s }: { s: BatterySuggestion }) {
  const { t } = useTranslation();
  const lines =
    (s.tipo === 'metrica' || s.tipo === 'consulta') && s.metrica
      ? [
          `+ ${t('assets.batteryRun.newMeasure')}: ${s.metrica.nome}`,
          `+   ${s.metrica.expressao}`,
          ...(s.metrica.sinonimos.length ? [`+   ${t('assets.batteryRun.synonyms')}: ${s.metrica.sinonimos.join(', ')}`] : []),
          ...(s.tipo === 'consulta' ? [`+ ${t('assets.batteryRun.validatedQuery')}: "${s.pergunta}"`, ...(s.exemplos?.length ? [`+   ${t('assets.batteryRun.alsoAsked')}: ${s.exemplos.join(' · ')}`] : [])] : []),
        ]
      : [`${t(`assets.sections.${s.secao}`)}`, `+ - ${s.texto}`];
  return (
    <pre className="overflow-x-auto whitespace-pre-wrap break-words rounded-[var(--lr-radius-md)] bg-sunken p-2 font-mono text-[length:var(--lr-text-xs)]">
      {lines.map((l) => (
        <span key={l} className={cn('block', l.startsWith('+') ? 'text-success' : 'text-fg-secondary')}>
          {l}
        </span>
      ))}
    </pre>
  );
}

/** Melhorias de uma rodada: marcar, levar ao rascunho (abre um se não há) ou recusar. A próxima rodada testa o rascunho. */
function Suggestions({ archive, detail }: { archive: BatteryArchive; detail: AssetDetail }) {
  const { t } = useTranslation();
  const k = 'assets.batteryRun';
  const [chosen, setChosen] = useState<Set<number>>(new Set());
  const create = useApiMutation('createVersion', [...ASSET_QUERIES]);
  const apply = useApiMutation('applyBatterySuggestions', [...ASSET_QUERIES, 'battery', 'assetBatteries']);
  const refuse = useApiMutation('refuseBatterySuggestions', ['battery', 'assetBatteries']);
  const publishOne = useApiMutation('publishBatterySuggestion', [...ASSET_QUERIES, 'battery', 'assetBatteries']);
  const revert = useApiMutation('revertBatterySuggestion', [...ASSET_QUERIES, 'battery', 'assetBatteries']);
  const isAdmin = Boolean(useSession().data?.is_admin);
  // Sugestão esperando a confirmação de publicar (o índice), com a diferença à vista.
  const [confirming, setConfirming] = useState<number | null>(null);
  const busy = create.isPending || apply.isPending || refuse.isPending || publishOne.isPending || revert.isPending;
  const open = archive.sugestoes.map((s, i) => ({ s, i })).filter(({ s }) => !s.estado);
  const indexes = [...chosen];
  const toDraft = async () => {
    try {
      const d = detail.draft ?? (await create.mutateAsync({ params: { id: detail.asset.id }, body: { from_version_id: detail.published?.id ?? null, reason: t(`${k}.draftReason`) } }));
      await apply.mutateAsync({ params: { id: d.version.id }, body: { run_id: archive.run_id, battery_id: archive.id, indexes, expected_updated_at: d.version.updated_at } });
      setChosen(new Set());
      toast.success(t(`${k}.applied`, { count: indexes.length }));
    } catch (err) {
      toast.error(errorMessage(t, err));
    }
  };
  if (!archive.sugestoes.length) return <p className="text-[length:var(--lr-text-sm)] text-fg-secondary">{t(`${k}.noSuggestions`)}</p>;
  return (
    <div className="flex flex-col gap-3">
      <ul className="flex flex-col gap-3">
        {archive.sugestoes.map((s, i) => (
          <li key={`${s.caso}-${i}`} className="flex gap-3 rounded-[var(--lr-radius-md)] border border-border p-3">
            <Checkbox
              className="mt-1"
              aria-label={t(`${k}.pickSuggestion`, { id: s.caso })}
              disabled={Boolean(s.estado)}
              checked={chosen.has(i)}
              onChange={(e) =>
                setChosen((cur) => {
                  const n = new Set(cur);
                  if (e.target.checked) n.add(i);
                  else n.delete(i);
                  return n;
                })
              }
            />
            <div className="flex min-w-0 flex-1 flex-col gap-2">
              <div className="flex flex-wrap items-center gap-2 text-[length:var(--lr-text-sm)]">
                <Badge tone="accent">{s.tipo === 'consulta' ? t(`${k}.validatedQuery`) : s.tipo === 'metrica' ? t(`${k}.newMeasure`) : t(`assets.sections.${s.secao}`)}</Badge>
                {s.verificada ? <StatusBadge tone="success">{t(`${k}.verified`)}</StatusBadge> : null}
                <span className="font-mono text-fg-muted">{s.caso}</span>
                {s.estado ? <StatusBadge tone={s.estado === 'publicada' ? 'success' : s.estado === 'aplicada' ? 'accent' : 'neutral'}>{t(`${k}.state.${s.estado}`)}</StatusBadge> : null}
              </div>
              <p className="break-words text-[length:var(--lr-text-sm)]">{s.pergunta}</p>
              {s.motivo ? <p className="break-words text-[length:var(--lr-text-xs)] text-fg-secondary">{s.motivo}</p> : null}
              <SuggestionDiff s={s} />
              {!s.estado ? (
                <div className="flex flex-wrap gap-2">
                  {isAdmin ? (
                    <Button size="sm" variant="primary" disabled={busy} onClick={() => setConfirming(i)}>
                      {t(`${k}.publishOne`)}
                    </Button>
                  ) : null}
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={busy}
                    onClick={() => refuse.mutate({ params: { id: archive.run_id, battery: archive.id }, body: { indexes: [i] } }, { onError: (err) => toast.error(errorMessage(t, err)) })}
                  >
                    {t(`${k}.refuseOne`)}
                  </Button>
                </div>
              ) : (s.estado === 'publicada' || s.estado === 'aplicada') && isAdmin ? (
                <div>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={busy}
                    onClick={() =>
                      revert.mutate(
                        { params: { id: archive.run_id, battery: archive.id }, body: { index: i } },
                        { onSuccess: () => toast.success(t(`${k}.reverted`)), onError: (err) => toast.error(errorMessage(t, err)) },
                      )
                    }
                  >
                    {t(`${k}.revert`)}
                  </Button>
                </div>
              ) : null}
            </div>
          </li>
        ))}
      </ul>
      {confirming !== null && archive.sugestoes[confirming] ? (
        <Dialog open onOpenChange={(o) => !o && setConfirming(null)}>
          <DialogContent title={t(`${k}.publishTitle`)} description={t(`${k}.publishHelp`)} closeLabel={t('common.close')}>
            <div className="flex flex-col gap-4">
              <SuggestionDiff s={archive.sugestoes[confirming]!} />
              {detail.draft ? <Alert tone="warning" title={t(`${k}.draftHasOther`)} /> : null}
              <div className="flex justify-end gap-2">
                <Button onClick={() => setConfirming(null)}>{t('common.cancel')}</Button>
                <Button
                  variant="primary"
                  disabled={publishOne.isPending}
                  onClick={() =>
                    publishOne.mutate(
                      { params: { id: archive.run_id, battery: archive.id }, body: { index: confirming } },
                      {
                        onSuccess: () => {
                          setConfirming(null);
                          toast.success(t(`${k}.published`));
                        },
                        onError: (err) => toast.error(errorMessage(t, err)),
                      },
                    )
                  }
                >
                  {publishOne.isPending ? t(`${k}.publishing`) : t(`${k}.publishNow`)}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      ) : null}
      {open.length ? (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" disabled={busy} onClick={() => setChosen(new Set(open.map((x) => x.i)))}>
            {t(`${k}.pickAll`)}
          </Button>
          <Button size="sm" variant="primary" disabled={busy || !indexes.length} onClick={() => void toDraft()}>
            {t(`${k}.toDraft`, { count: indexes.length })}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={busy || !indexes.length}
            onClick={() =>
              refuse.mutate(
                { params: { id: archive.run_id, battery: archive.id }, body: { indexes } },
                { onSuccess: () => setChosen(new Set()), onError: (err) => toast.error(errorMessage(t, err)) },
              )
            }
          >
            {t(`${k}.refuse`, { count: indexes.length })}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

type DetailData = { running: boolean; done: number; total: number; evaluated_at: string | null; score: number | null; critical_score: number | null; rows: Row[] };

/** Uma rodada aberta: barrinhas, nota, melhorias e perguntas. Serve à rodada em andamento e às guardadas. */
function BatteryDetail({ data, archive, detail }: { data: DetailData; archive: BatteryArchive | null; detail: AssetDetail }) {
  const { t } = useTranslation();
  const k = 'assets.batteryRun';
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState<Filter>('all');
  const [page, setPage] = useState(0);
  const [size, setSize] = useState<number>(10);
  const [search, setSearch] = useState('');
  // Clique numa barrinha: a tabela abre e mostra só aquela pergunta, até "Ver todas".
  const [picked, setPicked] = useState<string | null>(null);

  const needle = plain(search.trim());
  const all = data.rows;
  const rows = picked ? all.filter((r) => r.id === picked) : all.filter(MATCH[filter]).filter((r) => !needle || [r.id, r.pergunta, r.formula, r.formula_agente, r.categoria].some((x) => plain(x).includes(needle)));
  const pages = Math.max(1, Math.ceil(rows.length / size));
  const at = Math.min(page, pages - 1);
  const shown = rows.slice(at * size, at * size + size);
  const pct = data.total ? Math.round((100 * data.done) / data.total) : 0;
  const right = all.filter(MATCH.right).length;
  const wrong = all.filter(MATCH.wrong).length;
  const undecided = archive?.sugestoes.filter((x) => !x.estado).length ?? 0;

  if (!all.length) return <p className="text-[length:var(--lr-text-sm)] text-fg-secondary">{t(`${k}.empty`)}</p>;
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[length:var(--lr-text-sm)]" aria-live="polite">
          {data.running ? (
            <span className="font-medium">{t(`${k}.progress`, { done: data.done, total: data.total, pct })}</span>
          ) : data.score !== null ? (
            <StatusBadge tone={data.score >= MINIMUM ? 'success' : 'warning'}>{`${formatNumber(data.score, 1)}%`}</StatusBadge>
          ) : null}
          <span className="text-fg-secondary">{t(`${k}.tally`, { right, wrong })}</span>
          {archive && (archive.improved || archive.regressed) ? <span className="text-fg-secondary">{t(`${k}.trendShort`, { improved: archive.improved, regressed: archive.regressed })}</span> : null}
          {data.critical_score !== null && !data.running ? <span className="text-fg-secondary">{t(`${k}.criticalShort`, { score: formatNumber(data.critical_score, 1) })}</span> : null}
          {archive?.cycle ? <span className="text-fg-secondary">{t(`${k}.cycle`, { cycle: archive.cycle, cycles: archive.cycles })}</span> : null}
          {!data.running && data.evaluated_at ? <span className="text-fg-muted">{formatDateTime(data.evaluated_at)}</span> : null}
          {archive?.tokens ? (
            <InfoTip label={t(`${k}.costLabel`)} text={t(`${k}.tokens`, { calls: archive.tokens.calls, input: formatNumber(Math.round(archive.tokens.input / 1000)), output: formatNumber(Math.round(archive.tokens.output / 1000)) })} />
          ) : null}
        </div>
        <Segments
          rows={all}
          picked={picked}
          label={t(`${k}.segmentsLabel`, { right, wrong, total: all.length })}
          onPick={(r) => {
            setPicked(r.id);
            setPage(0);
            setOpen(true);
          }}
        />
      </div>

      {archive && !data.running ? (
        <div className="border-t border-border pt-3">
          <div className="-mx-2 flex items-center gap-1">
            <button
              type="button"
              aria-expanded={suggestionsOpen}
              aria-controls="bateria-melhorias"
              onClick={() => setSuggestionsOpen((o) => !o)}
              className={cn('flex flex-1 items-center gap-2 rounded-[var(--lr-radius-md)] px-2 py-1.5 text-left font-medium hover:bg-sunken', focusRing)}
            >
              <ChevronDown aria-hidden="true" className={cn('lr-nav-toggle-icon size-4 text-fg-muted', !suggestionsOpen && '-rotate-90')} />
              {t(`${k}.suggestionsShort`, { count: undecided })}
            </button>
            <InfoTip label={t(`${k}.aboutSuggestions`)} text={t(`${k}.suggestionsHelp`)} />
          </div>
          <div id="bateria-melhorias" className="lr-collapse" data-open={suggestionsOpen} inert={!suggestionsOpen}>
            <div>
              <div className="pt-3">
                <Suggestions key={archive.id} archive={archive} detail={detail} />
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <div className="border-t border-border pt-3">
        <button
          type="button"
          aria-expanded={open}
          aria-controls="bateria-perguntas"
          onClick={() => setOpen((o) => !o)}
          className={cn('-mx-2 flex w-[calc(100%+1rem)] items-center gap-2 rounded-[var(--lr-radius-md)] px-2 py-1.5 text-left font-medium hover:bg-sunken', focusRing)}
        >
          <ChevronDown aria-hidden="true" className={cn('lr-nav-toggle-icon size-4 text-fg-muted', !open && '-rotate-90')} />
          {t(`${k}.showQuestions`, { count: all.length })}
        </button>
        <div id="bateria-perguntas" className="lr-collapse" data-open={open} inert={!open}>
          <div>
            <div className="flex flex-col gap-4 pt-3">
              <div className="flex flex-wrap items-end gap-3">
                <div className="flex min-w-0 flex-[2_1_240px] flex-col gap-1.5">
                  <label htmlFor="bateria-busca" className="text-[length:var(--lr-text-sm)] font-medium text-fg">
                    {t(`${k}.search`)}
                  </label>
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-fg-muted" aria-hidden="true" />
                    <Input
                      id="bateria-busca"
                      type="search"
                      className="pl-9"
                      value={search}
                      placeholder={t(`${k}.searchPlaceholder`)}
                      autoComplete="off"
                      onChange={(e) => {
                        setSearch(e.target.value);
                        setPicked(null);
                        setPage(0);
                      }}
                    />
                  </div>
                </div>
                <Field id="bateria-filtro" label={t(`${k}.filter`)} className="min-w-0 flex-[1_1_180px]">
                  <NativeSelect
                    value={filter}
                    onChange={(e) => {
                      setFilter(e.target.value as Filter);
                      setPicked(null);
                      setPage(0);
                    }}
                  >
                    {(['all', 'wrong', 'right', 'pending'] as const).map((f) => (
                      <option key={f} value={f}>
                        {t(`${k}.filters.${f}`, { count: all.filter(MATCH[f]).length })}
                      </option>
                    ))}
                  </NativeSelect>
                </Field>
                <Field id="bateria-por-pagina" label={t(`${k}.perPage`)} className="w-28">
                  <NativeSelect
                    value={size}
                    onChange={(e) => {
                      setSize(Number(e.target.value));
                      setPage(0);
                    }}
                  >
                    {SIZES.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </NativeSelect>
                </Field>
              </div>
              {picked ? (
                <div className="flex flex-wrap items-center gap-2 text-[length:var(--lr-text-sm)]">
                  <span>{t(`${k}.onlyOne`, { id: picked })}</span>
                  <Button size="sm" variant="ghost" onClick={() => setPicked(null)}>
                    <X aria-hidden="true" />
                    {t(`${k}.showAll`)}
                  </Button>
                </div>
              ) : null}
              {rows.length === 0 ? <p className="text-[length:var(--lr-text-sm)] text-fg-secondary">{t(`${k}.noMatch`)}</p> : null}
              <div className="overflow-x-auto">
                <Table>
                  <TableCaption>{t(`${k}.title`)}</TableCaption>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[30%]">{t(`${k}.question`)}</TableHead>
                      <TableHead>{t(`${k}.expected`)}</TableHead>
                      <TableHead>{t(`${k}.agentFormula`)}</TableHead>
                      <TableHead>{t(`${k}.result`)}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {shown.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell className="align-top">
                          <p className="break-words">{r.pergunta}</p>
                          <p className="mt-1 flex flex-wrap gap-1.5 text-[length:var(--lr-text-xs)] text-fg-muted">
                            <span className="font-mono">{r.id}</span>
                            {r.categoria ? <Badge>{r.categoria}</Badge> : null}
                            {r.critico ? <Badge tone="warning">{t(`${k}.criticalTag`)}</Badge> : null}
                          </p>
                        </TableCell>
                        <TableCell className="max-w-72 align-top font-mono text-[length:var(--lr-text-xs)]">
                          {r.formula ? <span className="line-clamp-4 break-all">{r.formula}</span> : <span className="font-sans text-fg-muted">{t(`${k}.noFormula`)}</span>}
                        </TableCell>
                        <TableCell className="max-w-72 align-top font-mono text-[length:var(--lr-text-xs)]">
                          {r.formula_agente ? (
                            <span className="line-clamp-4 whitespace-pre-wrap break-all">{r.formula_agente}</span>
                          ) : (
                            <span className="font-sans text-fg-muted">{r.resultado === 'aguardando' ? t(`${k}.noFormulaYet`) : t(`${k}.noQuery`)}</span>
                          )}
                        </TableCell>
                        <TableCell className="align-top">
                          <span className="flex flex-wrap items-center gap-1">
                            <StatusBadge tone={TONE[r.resultado]}>{t(`${k}.results.${r.resultado}`)}</StatusBadge>
                            {r.tendencia && r.tendencia !== 'igual' ? <Badge tone={r.tendencia === 'melhorou' ? 'success' : 'danger'}>{t(`${k}.trends.${r.tendencia}`)}</Badge> : null}
                            {r.motivo ? <InfoTip label={t(`${k}.why`)} text={r.motivo} /> : null}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {pages > 1 ? (
                <nav aria-label={t(`${k}.pagination`)} className="flex flex-wrap items-center justify-end gap-2">
                  <Button size="sm" disabled={at === 0} onClick={() => setPage(at - 1)}>
                    <ChevronLeft aria-hidden="true" />
                    {t('apps.prev')}
                  </Button>
                  <span className="tabular text-[length:var(--lr-text-sm)]">{t('apps.pageOf', { page: at + 1, pages })}</span>
                  <Button size="sm" disabled={at >= pages - 1} onClick={() => setPage(at + 1)}>
                    {t('apps.next')}
                    <ChevronRight aria-hidden="true" />
                  </Button>
                </nav>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Lista das rodadas: uma linha por rodada, com o mini-gráfico; clicar abre o detalhe. */
function BatteryHistory({ list, onOpen }: { list: BatteryArchiveSummary[]; onOpen: (b: BatteryArchiveSummary) => void }) {
  const { t } = useTranslation();
  const k = 'assets.batteryRun';
  if (!list.length) return <p className="text-[length:var(--lr-text-sm)] text-fg-secondary">{t(`${k}.noRuns`)}</p>;
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableCaption>{t(`${k}.runsCaption`)}</TableCaption>
        <TableHeader>
          <TableRow>
            <TableHead>{t(`${k}.runWhen`)}</TableHead>
            <TableHead>{t(`${k}.runScore`)}</TableHead>
            <TableHead>{t(`${k}.runQuestions`)}</TableHead>
            <TableHead className="hidden sm:table-cell">{t(`${k}.runTrend`)}</TableHead>
            <TableHead className="w-12">
              <span className="sr-only">{t(`${k}.costLabel`)}</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {list.map((b) => (
            <TableRow
              key={`${b.run_id}:${b.id}`}
              tabIndex={0}
              onClick={() => onOpen(b)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onOpen(b);
                }
              }}
              className={cn('cursor-pointer hover:bg-sunken/60', focusRing)}
            >
              <TableCell className="whitespace-nowrap align-middle">
                <span className="tabular">{formatDateTime(b.started_at)}</span>
                <span className="mt-0.5 flex flex-wrap gap-1">
                  {b.status !== 'concluida' ? <Badge>{t(`${k}.archiveState.${b.status}`)}</Badge> : null}
                  {b.cycle ? <Badge tone="accent">{t(`${k}.cycleShort`, { cycle: b.cycle, cycles: b.cycles })}</Badge> : null}
                </span>
              </TableCell>
              <TableCell className="align-middle">
                {b.score !== null ? <StatusBadge tone={b.score >= MINIMUM ? 'success' : 'warning'}>{`${formatNumber(b.score, 1)}%`}</StatusBadge> : <span className="text-fg-muted">-</span>}
              </TableCell>
              <TableCell className="align-middle">
                <span className="flex flex-col gap-1">
                  {b.marks ? <MiniMarks marks={b.marks} label={t(`${k}.segmentsLabel`, { right: b.right, wrong: b.wrong, total: b.total })} /> : null}
                  <span className="tabular text-[length:var(--lr-text-xs)] text-fg-secondary">{t(`${k}.tally`, { right: b.right, wrong: b.wrong })}</span>
                </span>
              </TableCell>
              <TableCell className="hidden align-middle sm:table-cell">
                {b.improved || b.regressed ? (
                  <span className="tabular text-[length:var(--lr-text-sm)]">
                    <span className="text-success">+{b.improved}</span> <span className="text-danger">−{b.regressed}</span>
                  </span>
                ) : (
                  <span className="text-fg-muted">-</span>
                )}
              </TableCell>
              <TableCell className="align-middle" onClick={(e) => e.stopPropagation()}>
                {b.tokens ? <InfoTip label={t(`${k}.costLabel`)} text={t(`${k}.tokens`, { calls: b.tokens.calls, input: formatNumber(Math.round(b.tokens.input / 1000)), output: formatNumber(Math.round(b.tokens.output / 1000)) })} /> : null}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export function BatteryRun({ run, p, running, detail }: { run: ContextRunDetail; p: PipelineSummary; running: boolean; detail: AssetDetail }) {
  const { t } = useTranslation();
  const k = 'assets.batteryRun';
  const live = useApiQuery('batteryResults', { params: { id: run.run.id } }, { refetchInterval: (s) => (running || s.state.data?.running ? 2000 : false) });
  const history = useApiQuery('assetBatteries', { params: { id: run.run.asset_id } }, { refetchInterval: running ? 5000 : false });
  // Rodada aberta no detalhe (run_id:id); sem ela, a lista.
  const [openKey, setOpenKey] = useState<string | null>(null);
  const list = history.data ?? [];
  const keyOf = (b: { run_id: string; id: string }) => `${b.run_id}:${b.id}`;
  const selected = list.find((b) => keyOf(b) === openKey) ?? null;
  const archiveQ = useApiQuery('battery', { params: { id: selected?.run_id ?? '', battery: selected?.id ?? '' } }, { enabled: Boolean(selected) });
  const archive = selected && archiveQ.data && archiveQ.data.id === selected.id ? archiveQ.data : null;
  const start = useApiMutation('runContextRunBattery', ['contextRun', 'contextRuns', 'batteryResults', 'assetBatteries']);
  // A bateria chama a LLM em cada caso: só o admin dispara.
  const isAdmin = Boolean(useSession().data?.is_admin);
  const approved = p.battery?.by_status.aprovado ?? 0;
  const isRunning = Boolean(live.data?.running || running);
  const go = (learn: boolean) =>
    start.mutate({ params: { id: run.run.id }, body: learn ? { learn: true } : {} }, { onSuccess: () => setOpenKey(null), onError: (err) => toast.error(errorMessage(t, err)) });

  return (
    <Card>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-3">
          {selected && !isRunning ? (
            <Button size="sm" variant="ghost" className="-ml-2" onClick={() => setOpenKey(null)}>
              <ArrowLeft aria-hidden="true" />
              {t(`${k}.backToRuns`)}
            </Button>
          ) : null}
          <div className="flex min-w-0 flex-1 items-center gap-1.5">
            <h2 className="text-[length:var(--lr-text-md)] font-semibold leading-tight">{selected && !isRunning ? t(`${k}.runOf`, { when: formatDateTime(selected.started_at) }) : t(`${k}.title`)}</h2>
            <InfoTip label={t(`${k}.aboutBattery`)} text={t(`${k}.help`)} />
          </div>
          {isRunning ? (
            <CancelRunButton runId={run.run.id} />
          ) : isAdmin ? (
            <div className="flex shrink-0 items-center gap-2">
              <Button variant="secondary" disabled={approved === 0 || start.isPending || run.run.status === 'rodando'} onClick={() => go(true)}>
                {t(`${k}.learn`)}
              </Button>
              <InfoTip label={t(`${k}.aboutLearn`)} text={t(`${k}.learnHelp`)} />
              <Button variant="primary" disabled={approved === 0 || start.isPending || run.run.status === 'rodando'} onClick={() => go(false)}>
                <Play aria-hidden="true" />
                {t(`${k}.run`)}
              </Button>
            </div>
          ) : null}
        </div>

        {isRunning && live.data ? (
          <BatteryDetail data={live.data} archive={null} detail={detail} />
        ) : selected ? (
          archive ? (
            <BatteryDetail
              key={keyOf(selected)}
              data={{ running: false, done: archive.total, total: archive.total, evaluated_at: archive.finished_at, score: archive.score, critical_score: archive.critical_score, rows: archive.rows }}
              archive={archive}
              detail={detail}
            />
          ) : (
            <p className="text-[length:var(--lr-text-sm)] text-fg-secondary">{t('common.loading')}</p>
          )
        ) : (
          <BatteryHistory list={list} onOpen={(b) => setOpenKey(keyOf(b))} />
        )}
      </CardContent>
    </Card>
  );
}
